import { RESULTS } from 'Apps/Consultation/constants';

import antiBrassModalSwipeImg from 'assets/images/anti-brass-modal-swipe.png';
import suppGoalsModalDesktopTablet from 'assets/images/consultation/goals/dt-suppgoals-modal.jpg';
import suppGoalsModalMobile from 'assets/images/consultation/goals/m-suppgoals-modal.jpg';

import getMediaUrl from 'utils/getMediaUrl';

export const signupAcceptTermsTitle =
  'Before we get started, please confirm the following information.';

export const skincareScoring = {
  title: custumerFirstName => `${custumerFirstName}, here’s your skin profile!`,
  subtitle:
    "Based on today's answers, your skin requires targeted support in these key areas. Your formulas will reflect these exact needs.",
  getGoalValue: value => [
    {
      name: 'Skin Barrier',
      value: value.skin_barrier_skin,
      description:
        '**Skin Barrier** measures the way your skin reacts to external stressors. A strong skin barrier supports more resilient skin, whereas a weak skin barrier can cause dry, reactive skin that’s more prone to the effects of aging. We’ll add ingredients that help to strengthen and restore your skin barrier.',
      keyQuestions: `**Key questions that drive this score:**
  - Has your skin ever reacted negatively to a skincare or makeup product?
  - Does your skin experience tightness, dryness, itching, or redness?
  - What are your biggest skin concerns right now?`,
      minLabel: 'Healthy',
      maxLabel: 'Fragile',
    },
    {
      name: 'Stressors',
      value: value.stressors_skin,
      description:
        '**Stressors** measures factors like diet, daily stress level, and geo-aggressors. We’ll add ingredients that protect, nourish, and fight the effects of these stressors on your skin.',
      keyQuestions: `**Key questions that drive this score:**
  - What environmental factors are impacting your skin?
  - How often are you stressed out?
  - How would you describe your diet?`,
      minLabel: 'Low',
      maxLabel: 'High',
    },
    {
      name: 'Oiliness',
      value: value.oiliness_skin,
      description:
        '**Oiliness** measures how balanced or oily your skin is, which is related to your skin’s sebum level and proneness to blemishes. We’ll add hydrating and mattifying ingredients for skin that’s hydrated, not slick.',
      keyQuestions: `**Key questions that drive this score:**
  - How does your skin feel right after you wash it?
  - How would you describe your diet?
  - How often do you break out?`,
      minLabel: 'Balanced',
      maxLabel: 'Oily',
    },
    {
      name: 'Dryness',
      value: value.dryness_skin,
      description:
        '**Dryness** measures how balanced or dry your skin is, which is related to your skin’s hydration level and moisture barrier. We’ll add nourishing ingredients for skin that’s hydrated and soft.',
      keyQuestions: `**Key questions that drive this score:**
  - How does your skin feel at the end of the day?
  - How much water do you drink in a day?
  - Does your skin experience tightness, dryness, itching, or redness?`,
      minLabel: 'Balanced',
      maxLabel: 'Dry',
    },
    {
      name: 'Sensitivity',
      value: value.sensitivity_skin,
      description:
        '**Sensitivity** measures your skin’s tendency to appear red and to feel uncomfortable. We’ll add products that help soothe and minimize discomfort.',
      keyQuestions: `**Key questions that drive this score:**
  - Does your skin experience tightness, dryness, itching, or redness?
  - Has your skin ever reacted negatively to a skincare or makeup product?
  - How often are you stressed out?`,
      minLabel: 'Not Sensitive',
      maxLabel: 'Sensitive',
    },
    {
      name: 'Skin Maturity',
      value: value.skin_maturity_skin,
      description:
        '**Skin Maturity** measures the prominence of fine lines, wrinkles, and loss of firmness. Nothing can stop the impact of time, but we’ll add ingredients to your routine that boost cell turnover and promote healthier looking skin.',
      keyQuestions: `**Key questions that drive this score:**
  - How old are you?
  - What kind of wrinkles can we help with?
  - How firm is your skin?`,
      minLabel: 'Less Mature',
      maxLabel: 'More Mature',
    },
    {
      /* \u00AD is an invisible hyphen caracter and is used to mark the place where a line-break should happen */
      name: 'Hyper\u00ADpigmentation',
      value: value.hyperpigmentation_skin,
      description:
        '**Hyperpigmetation** measures your skin’s appearance of evenness. We’ll add ingredients that reduce the look of hyperpigmentation and keep your skin hydrated and more radiant looking.',
      keyQuestions: `**Key questions that drive this score:**
  - What are your biggest skin concerns right now?
  - What environmental factors are impacting your skin?
  - How does your skin react if you spend an afternoon in the sun without wearing SPF?`,
      minLabel: 'Even',
      maxLabel: 'Uneven',
    },
  ],
  category: RESULTS,
};

export const goToDecider = {
  title: 'Your consultation has been stored!',
  description: 'Any updated answers will be taken into account on your next formula.',
  ctaSeeSubscription: 'View Subscription',
  ctaGoToCart: 'Go to cart',
  ctaOrderNow: 'Order now',
};

export const posology = {
  title: 'Each product is custom formulated with ingredients tailored to your needs.',
  subtitle:
    'All Prose haircare products are free of parabens, mineral oils, sulfates, GMOs, and animal cruelty. All Prose supplements contain gluten-free wheat.',
  category: RESULTS,
  sectionLabels: {
    core: name => `${name}’s haircare products include:`,
    supplement: name => `${name}’s supplements include:`,
  },
};

export const skincareIngredients = {
  title: 'Each product is made to order with ingredients tailored to your needs.',
  subtitle:
    'All Prose skincare products are vegan, free of parabens, mineral oils, sulfates, GMOs, and animal cruelty.',
  category: RESULTS,
  sectionLabel: name => `${name}’s skincare products include:`,
};

export const goals = {
  antiBrassNotice:
    '*This benefit is only available in the conditioner, which will be tinted light purple',
  antiBrassModal: {
    image: antiBrassModalSwipeImg,
    imageAlt: 'A swipe of light purple conditioner.',
    headline: 'Meet your (purple!) conditioner',
    description:
      "Since you want to keep your light tones cool and bright, we're customizing your conditioner with our Anti-Brass formulation, which blends naturally-derived pigments and safe synthetic dyes to give your conditioner a light purple tint. It's gentle enough to use every wash with no fear of staining your hair, skin, or bathroom",
    buttonLabel: 'Got it!',
  },
  hairSupplementsNotice: '† These benefits are only available from hair supplements',
  hairSupplementsDisclaimerModal: {
    imageBackground: {
      desktop: suppGoalsModalDesktopTablet,
      mobile: suppGoalsModalMobile,
    },
    title: (
      <>
        Meet your Root Source<sup>®</sup> for longer, stronger, fuller hair<sup>*</sup>
      </>
    ),
    descriptionSupplements:
      'Our plant-based supplement system is personally customized to help grow your best hair in two hyper-targeted steps.',
    supplementsDisclaimer:
      '*These statements have not been evaluated by the Food and Drug Administration. This product is not intended to diagnose, treat, cure or prevent any disease.',
    buttonLabel: 'Got it!',
    studyPdf: getMediaUrl('supplements_clinical_study.pdf'),
  },
};
